import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import clinicalNoteService from './clinicalNoteService.js';

/**
 * Maximum length enforced by both workflow request DTOs.
 */
const MAX_WORKFLOW_NOTE_LENGTH = 2000;

/**
 * Convert backend enum-style values into readable labels.
 *
 * Example:
 *
 * READY_FOR_REVIEW -> Ready For Review
 */
function formatEnumLabel(value) {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .filter(Boolean)
    .map(
      (part) =>
        part.charAt(0).toUpperCase() +
        part.slice(1)
    )
    .join(' ');
}

/**
 * Extract the optimistic-lock version from a validation response.
 */
function getValidationVersion(validation) {
  const version =
    validation?.entityVersion;

  if (
    version === null ||
    version === undefined
  ) {
    return null;
  }

  return version;
}

/**
 * Workflow controls for a clinical note.
 *
 * Supported backend transitions:
 *
 * DRAFT
 *   -> READY_FOR_REVIEW
 *
 * READY_FOR_REVIEW
 *   -> DRAFT
 *
 * Permanent signing is intentionally handled by the separate
 * clinical-note signature workflow.
 */
export default function ClinicalNoteWorkflowPanel({
  clinicalNoteId,
  enabled = true,
  disabled = false,
  refreshToken = 0,
  onWorkflowChanged,
  onValidationChange,
  onVersionChange,
}) {
  const [
    validation,
    setValidation,
  ] = useState(null);

  const [
    loading,
    setLoading,
  ] = useState(false);

  const [
    actionLoading,
    setActionLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState('');

  const [
    successMessage,
    setSuccessMessage,
  ] = useState('');

  const [
    reviewSubmissionNote,
    setReviewSubmissionNote,
  ] = useState('');

  const [
    returnReason,
    setReturnReason,
  ] = useState('');

  const [
    returnReasonError,
    setReturnReasonError,
  ] = useState('');

  /**
   * Apply a validation/workflow response consistently.
   */
  const applyValidationResponse =
    useCallback(
      (response) => {
        if (!response) {
          return;
        }

        setValidation(
          response
        );

        if (
          response.entityVersion !==
            null &&
          response.entityVersion !==
            undefined &&
          typeof onVersionChange ===
            'function'
        ) {
          onVersionChange(
            response.entityVersion,
            response
          );
        }

        if (
          typeof onValidationChange ===
          'function'
        ) {
          onValidationChange(
            response
          );
        }
      },
      [
        onValidationChange,
        onVersionChange,
      ]
    );

  /**
   * Load the latest backend workflow/validation state.
   */
  const loadWorkflow =
    useCallback(
      async () => {
        if (
          !enabled ||
          !clinicalNoteId
        ) {
          return;
        }

        setLoading(true);
        setError('');

        try {
          const response =
            await clinicalNoteService
              .validateClinicalNote(
                clinicalNoteId
              );

          applyValidationResponse(
            response
          );
        } catch (loadError) {
          setError(
            loadError?.message ||
              'Clinical-note workflow information could not be loaded.'
          );
        } finally {
          setLoading(false);
        }
      },
      [
        clinicalNoteId,
        enabled,
        applyValidationResponse,
      ]
    );

  /**
   * Refresh whenever the parent signals that clinical documentation
   * or warning state has changed.
   */
  useEffect(() => {
    if (!enabled) {
      return;
    }

    loadWorkflow();
  }, [
    loadWorkflow,
    refreshToken,
    enabled,
  ]);

  /**
   * Perform a fresh validation immediately before moving the note to
   * READY_FOR_REVIEW.
   *
   * This is deliberate:
   *
   * - it obtains the latest optimistic-lock version
   * - it confirms canMoveToReview using the backend
   * - the transition service validates everything again
   *
   * The frontend therefore never decides clinical readiness itself.
   */
  async function submitForReview() {
    if (
      disabled ||
      actionLoading
    ) {
      return;
    }

    const normalizedNote =
      reviewSubmissionNote.trim();

    if (
      normalizedNote.length >
      MAX_WORKFLOW_NOTE_LENGTH
    ) {
      setError(
        'Review submission note cannot exceed 2000 characters.'
      );

      return;
    }

    setActionLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      /*
       * Preflight validation gets the exact current note version.
       */
      const preflight =
        await clinicalNoteService
          .validateClinicalNote(
            clinicalNoteId
          );

      applyValidationResponse(
        preflight
      );

      if (
        preflight?.status !==
        'DRAFT'
      ) {
        throw new Error(
          'Only a Draft clinical note can be submitted for review.'
        );
      }

      if (
        preflight
          ?.canMoveToReview !==
        true
      ) {
        const blockingCount =
          Number(
            preflight
              ?.blockingIssueCount ??
              0
          );

        if (
          preflight
            ?.hasUnresolvedWarnings
        ) {
          throw new Error(
            'Clinical warnings must be corrected or acknowledged before this note can be submitted for review.'
          );
        }

        if (
          blockingCount === 1
        ) {
          throw new Error(
            'The clinical note still has 1 blocking validation issue that must be corrected before review.'
          );
        }

        if (
          blockingCount > 1
        ) {
          throw new Error(
            `The clinical note still has ${blockingCount} blocking validation issues that must be corrected before review.`
          );
        }

        throw new Error(
          'The clinical note does not currently meet the requirements for review.'
        );
      }

      const expectedVersion =
        getValidationVersion(
          preflight
        );

      if (
        expectedVersion ===
        null
      ) {
        throw new Error(
          'The current clinical note version could not be determined.'
        );
      }

      const response =
        await clinicalNoteService
          .submitForReview(
            clinicalNoteId,
            {
              reviewSubmissionNote:
                normalizedNote ||
                null,

              expectedVersion,
            }
          );

      applyValidationResponse(
        response
      );

      setReviewSubmissionNote(
        ''
      );

      setSuccessMessage(
        'Clinical note submitted for review.'
      );

      if (
        typeof onWorkflowChanged ===
        'function'
      ) {
        onWorkflowChanged(
          response,
          {
            action:
              'READY_FOR_REVIEW',

            status:
              response?.status,
          }
        );
      }
    } catch (actionError) {
      setError(
        actionError?.message ||
          'Clinical note could not be submitted for review.'
      );

      /*
       * A concurrent autosave may have advanced the optimistic-lock
       * version. Refresh the workflow state after failure so the user
       * immediately sees the current backend result.
       */
      try {
        const refreshed =
          await clinicalNoteService
            .validateClinicalNote(
              clinicalNoteId
            );

        applyValidationResponse(
          refreshed
        );
      } catch {
        /*
         * Preserve the original workflow error.
         */
      }
    } finally {
      setActionLoading(false);
    }
  }

  /**
   * Return a READY_FOR_REVIEW note to DRAFT.
   *
   * A clinical reason is mandatory and becomes internal audit data.
   */
  async function returnToDraft() {
    if (
      disabled ||
      actionLoading
    ) {
      return;
    }

    const normalizedReason =
      returnReason.trim();

    if (!normalizedReason) {
      setReturnReasonError(
        'A return-to-draft reason is required.'
      );

      return;
    }

    if (
      normalizedReason.length >
      MAX_WORKFLOW_NOTE_LENGTH
    ) {
      setReturnReasonError(
        'Return-to-draft reason cannot exceed 2000 characters.'
      );

      return;
    }

    setReturnReasonError('');
    setError('');
    setSuccessMessage('');
    setActionLoading(true);

    try {
      /*
       * Obtain the latest workflow state and optimistic-lock version.
       */
      const preflight =
        await clinicalNoteService
          .validateClinicalNote(
            clinicalNoteId
          );

      applyValidationResponse(
        preflight
      );

      if (
        preflight?.status !==
        'READY_FOR_REVIEW'
      ) {
        throw new Error(
          'Only a Ready For Review clinical note can be returned to Draft.'
        );
      }

      const expectedVersion =
        getValidationVersion(
          preflight
        );

      if (
        expectedVersion ===
        null
      ) {
        throw new Error(
          'The current clinical note version could not be determined.'
        );
      }

      const response =
        await clinicalNoteService
          .returnToDraft(
            clinicalNoteId,
            {
              reason:
                normalizedReason,

              expectedVersion,
            }
          );

      applyValidationResponse(
        response
      );

      setReturnReason(
        ''
      );

      setSuccessMessage(
        'Clinical note returned to Draft for correction.'
      );

      if (
        typeof onWorkflowChanged ===
        'function'
      ) {
        onWorkflowChanged(
          response,
          {
            action:
              'RETURNED_TO_DRAFT',

            status:
              response?.status,
          }
        );
      }
    } catch (actionError) {
      setError(
        actionError?.message ||
          'Clinical note could not be returned to Draft.'
      );

      /*
       * Refresh after an optimistic-lock or workflow conflict while
       * preserving the user's entered return reason.
       */
      try {
        const refreshed =
          await clinicalNoteService
            .validateClinicalNote(
              clinicalNoteId
            );

        applyValidationResponse(
          refreshed
        );
      } catch {
        /*
         * Preserve the original transition error.
         */
      }
    } finally {
      setActionLoading(false);
    }
  }

  /**
   * Clear a return-reason error as soon as the user corrects it.
   */
  function updateReturnReason(
    value
  ) {
    setReturnReason(
      value
    );

    if (returnReasonError) {
      setReturnReasonError(
        ''
      );
    }
  }

  const status =
    validation?.status ||
    '';

  const isDraft =
    status === 'DRAFT';

  const isReadyForReview =
    status ===
    'READY_FOR_REVIEW';

  const isSigned =
    status === 'SIGNED';

  const reviewNoteLength =
    reviewSubmissionNote.length;

  const returnReasonLength =
    returnReason.length;

  const blockingCount =
    Number(
      validation
        ?.blockingIssueCount ??
        0
    );

  const unresolvedWarnings =
    Boolean(
      validation
        ?.hasUnresolvedWarnings
    );

  const readinessMessage =
    useMemo(() => {
      if (!validation) {
        return '';
      }

      if (
        validation.canMoveToReview
      ) {
        return 'All backend requirements for review are currently satisfied.';
      }

      if (
        validation.status !==
        'DRAFT'
      ) {
        return '';
      }

      if (
        unresolvedWarnings &&
        blockingCount > 0
      ) {
        return `${blockingCount} blocking validation ${
          blockingCount === 1
            ? 'issue remains'
            : 'issues remain'
        }, and unresolved clinical warnings also require attention.`;
      }

      if (
        unresolvedWarnings
      ) {
        return 'Clinical warnings must be corrected or acknowledged before review.';
      }

      if (
        blockingCount > 0
      ) {
        return `${blockingCount} blocking validation ${
          blockingCount === 1
            ? 'issue remains'
            : 'issues remain'
        } before review.`;
      }

      return 'The backend does not currently consider this note ready for review.';
    }, [
      validation,
      unresolvedWarnings,
      blockingCount,
    ]);

  if (!enabled) {
    return null;
  }

  return (
    <section
      style={{
        border:
          '1px solid #d7d7d7',
        borderRadius: 10,
        background: '#fff',
        overflow: 'hidden',
      }}
    >
      {/* =====================================================
          HEADER
          ===================================================== */}
      <div
        style={{
          display: 'flex',
          justifyContent:
            'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
          padding:
            '13px 16px',
          background:
            '#f7f8fa',
          borderBottom:
            '1px solid #ddd',
        }}
      >
        <div>
          <h3
            style={{
              margin: 0,
              fontSize: 17,
            }}
          >
            Clinical Note Workflow
          </h3>

          <div
            style={{
              marginTop: 3,
              fontSize: 12,
              color: '#666',
            }}
          >
            Review readiness and
            workflow transitions are
            controlled by Spring Boot.
          </div>
        </div>

        <button
          type="button"
          disabled={
            loading ||
            actionLoading
          }
          onClick={
            loadWorkflow
          }
        >
          {loading
            ? 'Refreshing…'
            : 'Refresh Workflow'}
        </button>
      </div>

      <div
        style={{
          padding: 16,
        }}
      >
        {/* ===================================================
            CURRENT STATUS
            =================================================== */}
        {validation && (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns:
                'repeat(auto-fit, minmax(150px, 1fr))',
              gap: 9,
              marginBottom: 15,
            }}
          >
            <div
              style={{
                border:
                  '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  textTransform:
                    'uppercase',
                  fontWeight: 700,
                  color: '#777',
                }}
              >
                Status
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {formatEnumLabel(
                  status
                ) || 'Unknown'}
              </div>
            </div>

            <div
              style={{
                border:
                  validation
                    .canMoveToReview
                    ? '1px solid #a8d5b0'
                    : '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
                background:
                  validation
                    .canMoveToReview
                    ? '#f4fbf5'
                    : '#fff',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  textTransform:
                    'uppercase',
                  fontWeight: 700,
                  color: '#777',
                }}
              >
                Ready For Review
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {validation
                  .canMoveToReview
                  ? 'Yes'
                  : 'No'}
              </div>
            </div>

            <div
              style={{
                border:
                  validation.canSign
                    ? '1px solid #a8d5b0'
                    : '1px solid #ddd',
                borderRadius: 8,
                padding: 10,
                background:
                  validation.canSign
                    ? '#f4fbf5'
                    : '#fff',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  textTransform:
                    'uppercase',
                  fontWeight: 700,
                  color: '#777',
                }}
              >
                Ready To Sign
              </div>

              <div
                style={{
                  marginTop: 4,
                  fontWeight: 700,
                }}
              >
                {validation.canSign
                  ? 'Yes'
                  : 'No'}
              </div>
            </div>
          </div>
        )}

        {/* ===================================================
            LOAD STATE
            =================================================== */}
        {loading &&
          !validation && (
          <div
            style={{
              color: '#666',
              fontSize: 13,
            }}
          >
            Loading clinical-note
            workflow…
          </div>
        )}

        {/* ===================================================
            ERROR
            =================================================== */}
        {error && (
          <div
            role="alert"
            style={{
              marginBottom: 14,
              border:
                '1px solid #b42318',
              borderRadius: 8,
              background:
                '#fff5f5',
              padding: 11,
            }}
          >
            <strong>
              Workflow action could not
              be completed.
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              {error}
            </div>
          </div>
        )}

        {/* ===================================================
            SUCCESS
            =================================================== */}
        {successMessage && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              border:
                '1px solid #a8d5b0',
              borderRadius: 8,
              background:
                '#f4fbf5',
              padding: 11,
              color: '#276738',
              fontSize: 13,
              fontWeight: 700,
            }}
          >
            {successMessage}
          </div>
        )}

        {/* ===================================================
            DRAFT -> READY FOR REVIEW
            =================================================== */}
        {validation &&
          isDraft && (
          <section
            style={{
              border:
                validation
                  .canMoveToReview
                  ? '1px solid #a8d5b0'
                  : '1px solid #ddd',
              borderRadius: 9,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding:
                  '10px 12px',
                background:
                  validation
                    .canMoveToReview
                    ? '#f4fbf5'
                    : '#fafafa',
                borderBottom:
                  '1px solid #ddd',
              }}
            >
              <strong>
                Submit For Review
              </strong>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  lineHeight: 1.45,
                  color: '#666',
                }}
              >
                {readinessMessage}
              </div>
            </div>

            <div
              style={{
                padding: 12,
              }}
            >
              <label
                htmlFor="clinical-review-submission-note"
                style={{
                  display: 'block',
                  marginBottom: 5,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Internal Review Note
                <span
                  style={{
                    marginLeft: 5,
                    color: '#777',
                    fontWeight: 400,
                  }}
                >
                  Optional
                </span>
              </label>

              <textarea
                id="clinical-review-submission-note"
                value={
                  reviewSubmissionNote
                }
                maxLength={
                  MAX_WORKFLOW_NOTE_LENGTH
                }
                disabled={
                  disabled ||
                  actionLoading
                }
                rows={4}
                placeholder="Optional internal note for the reviewer"
                onChange={(event) =>
                  setReviewSubmissionNote(
                    event.target.value
                  )
                }
                style={{
                  width: '100%',
                  boxSizing:
                    'border-box',
                  resize: 'vertical',
                  minHeight: 90,
                  padding: 9,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.45,
                  background:
                    disabled
                      ? '#f3f4f6'
                      : '#fff',
                }}
              />

              <div
                style={{
                  marginTop: 5,
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  alignItems: 'center',
                  gap: 10,
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    color:
                      reviewNoteLength >=
                      1900
                        ? '#8a6500'
                        : '#777',
                  }}
                >
                  {reviewNoteLength}/
                  {
                    MAX_WORKFLOW_NOTE_LENGTH
                  }
                </span>

                <button
                  type="button"
                  disabled={
                    disabled ||
                    actionLoading ||
                    !validation
                      .canMoveToReview
                  }
                  onClick={
                    submitForReview
                  }
                >
                  {actionLoading
                    ? 'Submitting…'
                    : 'Submit For Review'}
                </button>
              </div>

              {!validation
                .canMoveToReview && (
                <div
                  style={{
                    marginTop: 9,
                    fontSize: 12,
                    color: '#8a6500',
                  }}
                >
                  Use the Clinical Note
                  Validation panel to
                  review the specific
                  items that still need
                  attention.
                </div>
              )}
            </div>
          </section>
        )}

        {/* ===================================================
            READY FOR REVIEW -> DRAFT
            =================================================== */}
        {validation &&
          isReadyForReview && (
          <section
            style={{
              border:
                '1px solid #d6a100',
              borderRadius: 9,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                padding:
                  '10px 12px',
                background:
                  '#fff8df',
                borderBottom:
                  '1px solid #ddd',
              }}
            >
              <strong>
                Review State
              </strong>

              <div
                style={{
                  marginTop: 4,
                  fontSize: 12,
                  lineHeight: 1.45,
                }}
              >
                This note is currently
                Ready For Review.

                {' '}

                {validation.canSign
                  ? 'Backend validation currently allows permanent signing.'
                  : 'The note currently does not satisfy all signing requirements.'}
              </div>
            </div>

            <div
              style={{
                padding: 12,
              }}
            >
              <label
                htmlFor="clinical-return-to-draft-reason"
                style={{
                  display: 'block',
                  marginBottom: 5,
                  fontSize: 12,
                  fontWeight: 700,
                }}
              >
                Return To Draft Reason
                <span
                  aria-hidden="true"
                  style={{
                    marginLeft: 4,
                    color: '#b42318',
                  }}
                >
                  *
                </span>
              </label>

              <textarea
                id="clinical-return-to-draft-reason"
                value={
                  returnReason
                }
                maxLength={
                  MAX_WORKFLOW_NOTE_LENGTH
                }
                disabled={
                  disabled ||
                  actionLoading
                }
                rows={4}
                placeholder="Explain why this note requires correction"
                aria-invalid={
                  Boolean(
                    returnReasonError
                  )
                }
                onChange={(event) =>
                  updateReturnReason(
                    event.target.value
                  )
                }
                style={{
                  width: '100%',
                  boxSizing:
                    'border-box',
                  resize: 'vertical',
                  minHeight: 90,
                  padding: 9,
                  fontFamily: 'inherit',
                  fontSize: 13,
                  lineHeight: 1.45,
                  border:
                    returnReasonError
                      ? '1px solid #b42318'
                      : '1px solid #bbb',
                  borderRadius: 6,
                  background:
                    disabled
                      ? '#f3f4f6'
                      : '#fff',
                }}
              />

              <div
                style={{
                  marginTop: 5,
                  display: 'flex',
                  justifyContent:
                    'space-between',
                  gap: 10,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <span
                  style={{
                    fontSize: 10,
                    color:
                      returnReasonLength >=
                      1900
                        ? '#8a6500'
                        : '#777',
                  }}
                >
                  {returnReasonLength}/
                  {
                    MAX_WORKFLOW_NOTE_LENGTH
                  }
                </span>

                <button
                  type="button"
                  disabled={
                    disabled ||
                    actionLoading ||
                    !returnReason.trim()
                  }
                  onClick={
                    returnToDraft
                  }
                >
                  {actionLoading
                    ? 'Returning…'
                    : 'Return To Draft'}
                </button>
              </div>

              {returnReasonError && (
                <div
                  role="alert"
                  style={{
                    marginTop: 7,
                    fontSize: 12,
                    color: '#b42318',
                  }}
                >
                  {returnReasonError}
                </div>
              )}

              <div
                style={{
                  marginTop: 9,
                  fontSize: 11,
                  lineHeight: 1.45,
                  color: '#666',
                }}
              >
                Returning the note to
                Draft preserves the
                existing clinical
                documentation. The
                reason is retained in
                the internal audit
                history.
              </div>
            </div>
          </section>
        )}

        {/* ===================================================
            SIGNED
            =================================================== */}
        {validation &&
          isSigned && (
          <div
            style={{
              border:
                '1px solid #a8d5b0',
              borderRadius: 8,
              background:
                '#f4fbf5',
              padding: 12,
            }}
          >
            <strong>
              Clinical note signed
            </strong>

            <div
              style={{
                marginTop: 4,
                fontSize: 13,
                lineHeight: 1.45,
              }}
            >
              This note has completed
              the normal workflow and
              cannot be returned to
              Draft through this
              workflow panel.
            </div>
          </div>
        )}

        {/* ===================================================
            OTHER / UNKNOWN STATUS
            =================================================== */}
        {validation &&
          !isDraft &&
          !isReadyForReview &&
          !isSigned && (
          <div
            style={{
              border:
                '1px solid #ddd',
              borderRadius: 8,
              background:
                '#fafafa',
              padding: 12,
              fontSize: 13,
            }}
          >
            No workflow action is
            available for status{' '}
            <strong>
              {formatEnumLabel(
                status
              ) || 'Unknown'}
            </strong>
            .
          </div>
        )}

        {/* ===================================================
            READ ONLY
            =================================================== */}
        {disabled &&
          validation &&
          !isSigned && (
          <div
            style={{
              marginTop: 12,
              fontSize: 12,
              color: '#666',
            }}
          >
            Workflow actions are
            currently read only.
          </div>
        )}

        {validation?.entityVersion !==
          null &&
          validation?.entityVersion !==
            undefined && (
          <div
            style={{
              marginTop: 12,
              textAlign: 'right',
              fontSize: 10,
              color: '#999',
            }}
          >
            Clinical note version{' '}
            {
              validation.entityVersion
            }
          </div>
        )}
      </div>
    </section>
  );
}